import Button from '@/common/components/atom/button';
import { sendCenterProfileEvent, sendDoctorProfileEvent } from '@/modules/profile/events/nightBatEvents';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

const ErrorPage = ({
  statusCode,
  refresh,
  message,
  slug,
  pageType = 'doctor',
}: {
  message?: string;
  statusCode?: number;
  refresh: () => void;
  slug?: string;
  /** Center pages must not emit doctor-profile analytics. */
  pageType?: 'doctor' | 'center';
}) => {
  const router = useRouter();
  const resolvedSlug = slug ?? (typeof router.query.slug === 'string' ? decodeURIComponent(router.query.slug) : undefined);

  useEffect(() => {
    if (pageType === 'center') {
      sendCenterProfileEvent('center_profile_error', 'profile_error_client_side', {
        handle_error_status: statusCode ?? null,
        http_status: statusCode ?? null,
        slug: resolvedSlug ?? null,
        path: router.asPath,
        message: message ?? null,
      });
      sendCenterProfileEvent('center_profile_render_result', 'error', {
        slug: resolvedSlug ?? null,
        http_status: statusCode ?? null,
        source: 'error_page',
      });
      return;
    }

    sendDoctorProfileEvent('profile_error', 'profile_error_client_side', {
      handle_error_status: statusCode ?? null,
      http_status: statusCode ?? null,
      slug: resolvedSlug ?? null,
      path: router.asPath,
      message: message ?? null,
    });
    sendDoctorProfileEvent('profile_render_result', 'error', {
      slug: resolvedSlug ?? null,
      http_status: statusCode ?? null,
      source: 'error_page',
    });
  }, [statusCode, resolvedSlug, router.asPath, message, pageType]);

  const errors: { [key: number]: { status: number; title: string; message: string } } = {
    404: {
      status: 404,
      title: 'صفحه یافت نشد',
      message: 'متاسفیم، اما صفحه‌ای که دنبال آن هستید وجود ندارد.',
    },
    410: {
      status: 410,
      title: 'صفحه در دسترس نیست',
      message: 'این صفحه حذف شده یا دیگر در دسترس نیست.',
    },
    500: {
      status: 500,
      title: 'مشکلی پیش آمده است',
      message: 'مشکلی در سرور رخ داده است. لطفاً بعداً دوباره امتحان کنید.',
    },
    502: {
      status: 502,
      title: 'مشکلی پیش آمده است',
      message: 'مشکلی در سرور رخ داده است. لطفاً بعداً دوباره امتحان کنید.',
    },
    503: {
      status: 503,
      title: 'اطلاعات پزشک در حال به‌روزرسانی است',
      message: 'این صفحه موقتاً در دسترس نیست. لطفاً کمی بعد دوباره تلاش کنید.',
    },
    504: {
      status: 504,
      title: 'زمان درخواست به پایان رسید',
      message: 'سرور به موقع پاسخ نداد. لطفاً بعداً دوباره امتحان کنید.',
    },
  };

  const error: any = statusCode ? errors[statusCode ?? 500] || errors[500] : {};

  return (
    <div className="flex flex-col items-center justify-center flex-grow bg-gray-100 text-center p-6">
      {error.status && <h1 className="text-6xl font-bold text-red-600">{error?.status}</h1>}
      {error.title && <h2 className="text-3xl font-semibold mt-4">{error?.title}</h2>}
      <p className="text-lg text-gray-600 mt-2">{message || error?.message}</p>
      <div className="flex gap-3">
        <Button className="mt-6" onClick={refresh}>
          تلاش مجدد
        </Button>
        {(error.status === 404 || error.status === 410 || error.status === 503) && (
          <Button variant="secondary" className="mt-6" onClick={() => router.push('/')}>
            بازگشت به صفحه اصلی
          </Button>
        )}
      </div>
    </div>
  );
};
export default ErrorPage;
